import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, ApiClientError } from '../api/client';

// ─── 타입 ────────────────────────────────────────────────────────────────────

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

interface UseApiOptions {
  immediate?: boolean; // 마운트 시 즉시 호출 여부 (기본: true)
}

// ─── useApi (GET 자동 호출) ──────────────────────────────────────────────────

export function useApi<T>(
  path: string | null,
  options: UseApiOptions = { immediate: true }
) {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    isLoading: path !== null && options.immediate !== false,
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const fetch = useCallback(async () => {
    if (path === null) {
      setState({ data: null, isLoading: false, error: null });
      return null;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await apiClient.get<T>(path);
      setState({ data, isLoading: false, error: null });
      return data;
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : '데이터를 불러오는 중 오류가 발생했습니다.';
      setState({ data: null, isLoading: false, error: message });
      return null;
    }
  }, [path]);

  useEffect(() => {
    if (options.immediate !== false) {
      void fetch();
    }
    return () => {
      abortRef.current?.abort();
    };
  }, [fetch, options.immediate]);

  return { ...state, refetch: fetch };
}

// ─── useMutation (POST/PUT/DELETE) ──────────────────────────────────────────

type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface UseMutationState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

export function useMutation<TData, TPayload = unknown>(
  path: string,
  method: MutationMethod = 'POST'
) {
  const [state, setState] = useState<UseMutationState<TData>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const mutate = useCallback(
    async (payload?: TPayload): Promise<TData | null> => {
      setState({ data: null, isLoading: true, error: null });

      try {
        let data: TData;
        switch (method) {
          case 'POST':
            data = await apiClient.post<TData>(path, payload);
            break;
          case 'PUT':
            data = await apiClient.put<TData>(path, payload);
            break;
          case 'PATCH':
            data = await apiClient.patch<TData>(path, payload);
            break;
          case 'DELETE':
            data = await apiClient.delete<TData>(path);
            break;
        }
        setState({ data, isLoading: false, error: null });
        return data;
      } catch (err) {
        const message =
          err instanceof ApiClientError
            ? err.message
            : '요청 처리 중 오류가 발생했습니다.';
        setState({ data: null, isLoading: false, error: message });
        return null;
      }
    },
    [path, method]
  );

  return { ...state, mutate };
}
